import React from 'react';
import { usePage } from '@inertiajs/react';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useInertiaData from '@/hooks/useInertiaData';

interface AdminLink {
    label: string;
    url: string;
    primary?: boolean;
}

interface AdminPermissions {
    change_course: boolean;
    change_section: boolean;
    change_topic: boolean;
    change_courseclass: boolean;
}

const ADMIN_BASE = '/admin';

function buildAdminUrl(app: string, model: string, id: number): string {
    return `${ADMIN_BASE}/${app}/${model}/${id}/change/`;
}

function getAdminLinks(
    component: string,
    props: Record<string, unknown>,
    permissions: AdminPermissions,
    t: (key: string) => string
): AdminLink[] {
    const course = props.course as { id?: number } | undefined;
    const section = props.section as { id?: number } | undefined;
    const topic = props.topic as { id?: number } | undefined;
    const classData = (props.class ?? props.classData) as { id?: number } | undefined;

    switch (component) {
        case 'Courses/CourseDetail': {
            if (!course?.id || !permissions.change_course) return [];
            return [
                { label: t('adminBar.course'), url: buildAdminUrl('courses', 'course', course.id), primary: true },
            ];
        }
        case 'Courses/SectionDetail': {
            const links: AdminLink[] = [];
            if (section?.id && permissions.change_section) {
                links.push({ label: t('adminBar.section'), url: buildAdminUrl('courses', 'section', section.id), primary: true });
            }
            if (course?.id && permissions.change_course) {
                links.push({ label: t('adminBar.course'), url: buildAdminUrl('courses', 'course', course.id) });
            }
            return links;
        }
        case 'Courses/TopicDetail': {
            const links: AdminLink[] = [];
            if (topic?.id && permissions.change_topic) {
                links.push({ label: t('adminBar.topic'), url: buildAdminUrl('courses', 'topic', topic.id), primary: true });
            }
            if (section?.id && permissions.change_section) {
                links.push({ label: t('adminBar.section'), url: buildAdminUrl('courses', 'section', section.id) });
            }
            if (course?.id && permissions.change_course) {
                links.push({ label: t('adminBar.course'), url: buildAdminUrl('courses', 'course', course.id) });
            }
            return links;
        }
        case 'Courses/ClassDetail': {
            const links: AdminLink[] = [];
            if (classData?.id && permissions.change_courseclass) {
                links.push({ label: t('adminBar.class'), url: buildAdminUrl('courses', 'courseclass', classData.id), primary: true });
            }
            if (topic?.id && permissions.change_topic) {
                links.push({ label: t('adminBar.topic'), url: buildAdminUrl('courses', 'topic', topic.id) });
            }
            if (section?.id && permissions.change_section) {
                links.push({ label: t('adminBar.section'), url: buildAdminUrl('courses', 'section', section.id) });
            }
            if (course?.id && permissions.change_course) {
                links.push({ label: t('adminBar.course'), url: buildAdminUrl('courses', 'course', course.id) });
            }
            return links;
        }
        default:
            return [];
    }
}

const AdminBar: React.FC = () => {
    const { t } = useTranslation();
    const { admin_permissions } = useInertiaData();
    const { component, props } = usePage();

    if (!admin_permissions) return null;

    const links = getAdminLinks(component, props as Record<string, unknown>, admin_permissions, t);

    if (links.length === 0) return null;

    const primaryLink = links.find((l) => l.primary) ?? links[0];
    const secondaryLinks = links.filter((l) => l !== primaryLink);

    return (
        <div className="flex h-8 w-full items-center gap-2 bg-gray-800 px-3 text-xs text-gray-300">
            <ExternalLink size={14} className="shrink-0 text-gray-400" />
            <span className="text-gray-400">{t('adminBar.editInAdmin')}:</span>
            <a
                href={primaryLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-white hover:text-blue-300"
            >
                {primaryLink.label}
            </a>
            {secondaryLinks.map((link) => (
                <React.Fragment key={link.url}>
                    <span className="text-gray-600">&middot;</span>
                    <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-300"
                    >
                        {link.label}
                    </a>
                </React.Fragment>
            ))}
        </div>
    );
};

export default AdminBar;
